import {CaptureVariableIcon} from "../icons/CaptureVariableIcon";
import {Avatar, AvatarImage, AvatarFallback} from "./avatar";
import {Icon} from "./icon";
import {
  LogEvent,
  EventComponentProps,
  StateExecutedEvent,
  UserInputEvent,
  SlotCollectedEvent,
  BranchSelectedEvent,
  ActionExecutedEvent,
  ActionCompletedEvent,
  TransferInitiatedEvent,
  TransferFailedEvent,
  UtteranceSentEvent,
  UtteranceInterruptedEvent,
  MessageBufferedEvent,
  TurnChangedEvent,
  StateTransitionEvent,
  CommandReceivedEvent,
  FsmStartedEvent,
} from "@/types/logEvents";
import {
  ArrowLeftRight,
  ArrowRightToLine,
  Check,
  Info,
  MessagesSquare,
  PhoneCall,
  RefreshCcwIcon,
  Terminal,
  Workflow,
  Zap,
} from "lucide-react";
import {Tooltip, TooltipContent, TooltipTrigger} from "./tooltip";
import React from "react";

const EventWrapper = ({
  children,
  bgColor,
}: {
  children: React.ReactNode;
  bgColor: string;
}) => {
  return (
    <div className={`${bgColor} rounded-r flex flex-col gap-1`}>
      <div>{children}</div>
    </div>
  );
};

const StateExecutedEventComponent = ({
  event,
  interruptedMessage = "",
}: EventComponentProps) => {
  const stateEvent = event as StateExecutedEvent;
  const isInterrupted = interruptedMessage !== "";
  const message = stateEvent.details.message.slice(
    interruptedMessage.length,
    stateEvent.details.message.length,
  );

  return (
    <div className="p-3 rounded-xl rounded-tl-1 border">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Avatar className="w-5 h-5 rounded">
            <AvatarImage src="https://github.com/shadcn.png" />
            <AvatarFallback className="rounded-1">CN</AvatarFallback>
          </Avatar>
          <div className="text-sm font-medium text-secondary">
            Toni AI Agent
          </div>
        </div>
        <div
          className={`text-sm leading-4.5 ${
            isInterrupted ? "text-orange" : "text-primary"
          }`}
        >
          <span className="text-primary">{interruptedMessage}</span>
          {message}
        </div>
      </div>
    </div>
  );
};

const UserInputEventComponent = ({event}: EventComponentProps) => {
  const userEvent = event as UserInputEvent;
  return (
    <div className="p-3 rounded-xl rounded-tr-1 bg-primary bg-opacity-10 items-end gap-2 float-right inline-block ml-auto">
      <div className="text-sm leading-4.5 text-primary-accent text-left">
        {userEvent.details.message}
      </div>
    </div>
  );
};

const CallStartedEventComponent = () => (
  <div className="flex items-end gap-2 text-sm">
    <div className="text-green flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 border-0 text-green">
        <PhoneCall />
      </Icon>
      Call Started
    </div>
  </div>
);

const SlotCollectedEventComponent = ({event}: EventComponentProps) => {
  const slotEvent = event as SlotCollectedEvent;
  return (
    <div className="flex flex-col text-sm">
      <div className="text-green flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 border-0 text-green">
          <CaptureVariableIcon strokeWidth={2.5} className="w-3.5 h-3.5" />
        </Icon>
        <span className="font-medium">{slotEvent.details.slot_name}</span>{" "}
        variable collected
        <Tooltip delayDuration={0} disableHoverableContent={true}>
          <TooltipTrigger asChild>
            <Icon className="w-3 h-3 border-0 text-green ml-1 cursor-pointer">
              <Info />
            </Icon>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={10}>
            <div className="flex flex-col gap-1 text-secondary">
              <div className="flex flex-row gap-1">
                <div>Value:</div>
                <div className="text-white">{slotEvent.details.slot_value}</div>
              </div>
              <div className="flex flex-row gap-1">
                <div>Type:</div>
                <div className="text-white">{slotEvent.details.slot_type}</div>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

const BranchSelectedEventComponent = ({event}: EventComponentProps) => {
  const branchEvent = event as BranchSelectedEvent;
  return (
    <div className="flex flex-col text-sm">
      <div className="text-secondary flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 border-0 text-secondary">
          <Workflow strokeWidth={2.5} className="w-3.5 h-3.5" />
        </Icon>
        <span className="font-medium">{branchEvent.details.slot_name}</span>{" "}
        branch selected
        <Tooltip delayDuration={0} disableHoverableContent={true}>
          <TooltipTrigger asChild>
            <Icon className="w-3 h-3 border-0 text-secondary ml-1 cursor-pointer">
              <Info />
            </Icon>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            sideOffset={10}
            className="bg-slate-800"
          >
            <div className="flex flex-col gap-1 text-secondary">
              <div className="flex flex-row gap-1">
                <div>Value:</div>
                <div className="text-white">
                  {branchEvent.details.slot_value}
                </div>
              </div>
              <div className="flex flex-row gap-1">
                <div>Type:</div>
                <div className="text-white">
                  {branchEvent.details.slot_type}
                </div>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

const FsmStartedEventComponent = ({event}: EventComponentProps) => {
  const fsmEvent = event as FsmStartedEvent;
  return (
    <div className="flex items-end gap-2 text-sm">
      <div className="font-medium text-blue-800 flex items-center gap-1">
        <Icon className="w-3.5 h-3.5 border-0 text-blue-800">
          <ArrowRightToLine />
        </Icon>
        FSM Started
      </div>
      <div className="text-blue-600">{fsmEvent.details.fsm_name}</div>
    </div>
  );
};

const CommandReceivedEventComponent = ({event}: EventComponentProps) => {
  const cmdEvent = event as CommandReceivedEvent;
  return (
    <div className="flex items-end gap-2 text-sm">
      <div className="font-medium text-orange-800 flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-orange-800">
          <Terminal />
        </Icon>
        {cmdEvent.details.command_type}
      </div>
      <div className="text-orange-600">{cmdEvent.details.parameters[0]}</div>
    </div>
  );
};

const StateTransitionEventComponent = ({event}: EventComponentProps) => {
  const transitionEvent = event as StateTransitionEvent;
  return (
    <div className="flex flex-col text-sm gap-1">
      <div className="font-medium text-indigo-800 flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-indigo-800">
          <ArrowRightToLine />
        </Icon>
        <b>from:</b> {transitionEvent.details.from_state} <b>to:</b>{" "}
        {transitionEvent.details.to_state}
      </div>
      <div className="text-muted-foreground text-xs flex items-center gap-1">
        <div className="font-medium text-muted-foreground opacity-50">
          Trigger:
        </div>
        <div className="text-primary font-medium">
          {transitionEvent.details.trigger}
        </div>
      </div>
    </div>
  );
};

const ActionExecutedEventComponent = ({event}: EventComponentProps) => {
  const actionEvent = event as ActionExecutedEvent;
  return (
    <div className="flex flex-col text-sm">
      <div className="font-medium text-blue flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-blue">
          <Zap />
        </Icon>
        <b>{actionEvent.details.action_name}</b> action executed
      </div>
      <div className="flex flex-row gap-1 text-secondary font-normal text-sm overflow-hidden whitespace-nowrap text-ellipsis">
        {actionEvent.details.account_id && (
          <div>{actionEvent.details.account_id}</div>
        )}
        {actionEvent.details.balance && (
          <div className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-secondary before:text-lg before:font-bold before:!leading-5">
            {actionEvent.details.balance}
          </div>
        )}
      </div>
    </div>
  );
};

const UtteranceSentEventComponent = ({event}: EventComponentProps) => {
  const utteranceEvent = event as UtteranceSentEvent;
  return (
    <div className="p-3 rounded-xl rounded-tl-1 border inline-block float-left">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Avatar className="w-5 h-5 rounded-1">
            <AvatarImage src="https://github.com/shadcn.png" />
            <AvatarFallback className="rounded-1">CN</AvatarFallback>
          </Avatar>
          <div className="text-sm font-medium text-secondary">
            Toni AI Agent
          </div>
        </div>
        <div className="text-sm leading-4.5 text-primary">
          {utteranceEvent.details.utterance}
        </div>
      </div>
    </div>
  );
};

const UtteranceInterruptedEventComponent = ({event}: EventComponentProps) => {
  const interruptedEvent = event as UtteranceInterruptedEvent;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-orange flex items-center gap-1.5 text-sm">
        <Icon className="w-3.5 h-3.5 border-0 text-orange">
          <MessagesSquare strokeWidth={2.5} className="w-3.5 h-3.5" />
        </Icon>
        <span>Interrupted by user</span>
      </div>
      <UserInputEventComponent
        event={
          {
            ...event,
            details: {message: interruptedEvent.details.user_message},
            type: "user_input",
          } as UserInputEvent
        }
        interruptedMessage=""
      />
    </div>
  );
};

const TransferInitiatedEventComponent = ({event}: EventComponentProps) => {
  const transferEvent = event as TransferInitiatedEvent;
  return (
    <div className="flex flex-col text-sm gap-1">
      <div className="font-medium text-yellow flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-yellow">
          <ArrowLeftRight />
        </Icon>
        <b>{transferEvent.details.transfer_name}</b> transfer initiated
      </div>
      <div className="text-muted-foreground text-xs flex items-center gap-1">
        <div className="font-medium text-muted-foreground opacity-50">
          Reason:
        </div>
        <div className="text-primary font-medium">
          {transferEvent.details.reason}
        </div>
        <div className="font-medium text-muted-foreground opacity-50">
          Type:
        </div>
        <div className="text-primary font-medium">
          {transferEvent.details.transfer_type}
        </div>
      </div>
    </div>
  );
};

const TransferFailedEventComponent = ({event}: EventComponentProps) => {
  const failedEvent = event as TransferFailedEvent;
  return (
    <div className="flex flex-col text-sm gap-1">
      <div className="font-medium text-red flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-red">
          <ArrowLeftRight />
        </Icon>
        <b>{failedEvent.error}</b> transfer failed
      </div>
      <div className="text-muted-foreground text-xs flex items-center gap-1">
        <div className="font-medium text-muted-foreground opacity-50">
          fallback:
        </div>
        <div className="text-primary font-medium">
          {failedEvent.details.fallback}
        </div>
        <div className="font-medium text-muted-foreground opacity-50">
          retries:
        </div>
        <div className="text-primary font-medium">
          {failedEvent.details.retry_count}
        </div>
      </div>
    </div>
  );
};

const MessageBufferedEventComponent = ({event}: EventComponentProps) => {
  const bufferedEvent = event as MessageBufferedEvent;
  return (
    <div className="flex flex-col text-sm gap-1">
      <div className="font-medium text-azure-600 flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-azure-600">
          <RefreshCcwIcon />
        </Icon>
        <b>Message Buffered</b>
      </div>
      <div className="text-muted-foreground text-xs flex items-center gap-1">
        <div className="font-medium text-muted-foreground opacity-50">
          Reason:
        </div>
        <div className="text-primary font-medium">
          {bufferedEvent.details.buffer_reason}
        </div>
      </div>
    </div>
  );
};

const TurnChangedEventComponent = ({event}: EventComponentProps) => {
  const turnEvent = event as TurnChangedEvent;
  return (
    <div className="flex flex-col text-sm gap-1">
      <div className="font-medium text-slate-600 flex items-center gap-1">
        <Icon className="w-4 h-4 border-0 text-slate-600">
          <Workflow />
        </Icon>
        <b>from:</b> {turnEvent.details.previous_turn} <b>to:</b>{" "}
        {turnEvent.details.new_turn}
      </div>
      <div className="text-muted-foreground text-xs flex items-center gap-1">
        <div className="font-medium text-muted-foreground opacity-50">
          type:
        </div>
        <div className="text-primary font-medium">
          {turnEvent.details.turn_type}
        </div>
      </div>
    </div>
  );
};

const CallEndedEventComponent = () => (
  <div className="flex flex-col text-sm gap-1">
    <div className="font-medium text-red flex items-center gap-1">
      <Icon className="w-4 h-4 border-0 text-red">
        <PhoneCall />
      </Icon>
      Call Ended
    </div>
  </div>
);

const CallTransferredEventComponent = () => (
  <div className="flex flex-col text-sm gap-1">
    <div className="font-medium text-yellow flex items-center gap-1">
      <Icon className="w-4 h-4 border-0 text-yellow">
        <ArrowLeftRight />
      </Icon>
      Call Transferred
    </div>
  </div>
);

const ActionCompletedEventComponent = ({event}: EventComponentProps) => {
  const completedEvent = event as ActionCompletedEvent;
  return (
    <div className="flex flex-col text-sm">
      <div className="text-blue flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 border-0 text-blue">
          <Zap />
        </Icon>
        <span className="font-medium">
          {completedEvent.details.action_name}
        </span>{" "}
        action completed
        <Tooltip delayDuration={0} disableHoverableContent={true}>
          <TooltipTrigger asChild>
            <Icon className="w-3 h-3 border-0 text-blue ml-1 cursor-pointer">
              <Info />
            </Icon>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            sideOffset={10}
            className="bg-slate-800"
          >
            <div className="flex flex-col gap-1 text-secondary">
              <div className="flex flex-row gap-1">
                <div>Value:</div>
                <div className="text-white">
                  {completedEvent.details.slot_value}
                </div>
              </div>
              <div className="flex flex-row gap-1">
                <div>Type:</div>
                <div className="text-white">
                  {completedEvent.details.slot_type}
                </div>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

const TransferCompletedEventComponent = () => (
  <div className="flex flex-col text-sm gap-1">
    <div className="font-medium text-green flex items-center gap-1">
      <Icon className="w-4 h-4 border-0 text-green">
        <Check />
      </Icon>
      Transfer Completed
    </div>
  </div>
);

const eventComponents: Record<
  string,
  React.ComponentType<EventComponentProps>
> = {
  call_started: CallStartedEventComponent,
  branch_selected: BranchSelectedEventComponent,
  fsm_started: FsmStartedEventComponent,
  state_executed: StateExecutedEventComponent,
  user_input: UserInputEventComponent,
  command_received: CommandReceivedEventComponent,
  state_transition: StateTransitionEventComponent,
  slot_collected: SlotCollectedEventComponent,
  action_executed: ActionExecutedEventComponent,
  utterance_sent: UtteranceSentEventComponent,
  utterance_interrupted: UtteranceInterruptedEventComponent,
  transfer_initiated: TransferInitiatedEventComponent,
  transfer_failed: TransferFailedEventComponent,
  message_buffered: MessageBufferedEventComponent,
  turn_changed: TurnChangedEventComponent,
  call_ended: CallEndedEventComponent,
  call_transferred: CallTransferredEventComponent,
  action_completed: ActionCompletedEventComponent,
  transfer_completed: TransferCompletedEventComponent,
};

export default function Logs({
  events,
  showEvents,
}: {
  events: LogEvent[];
  showEvents: boolean;
}) {
  return (
    <>
      {events.map((event, index) => {
        if (
          !showEvents &&
          event.type !== "user_input" &&
          event.type !== "state_executed"
        ) {
          return null;
        }

        const EventComponent = eventComponents[event.type];
        if (!EventComponent) {
          return (
            <EventWrapper key={index} bgColor="bg-gray-50">
              <div className="p-1 rounded-r flex items-end gap-2">
                <div className="text-sm font-medium text-gray-800">
                  Unknown Event
                </div>
                <div className="text-xs leading-4.5 text-gray-600">
                  {event.type}
                </div>
              </div>
            </EventWrapper>
          );
        }

        const nextEvent = events[index + 1];
        const isNextEventInterrupted =
          nextEvent &&
          (nextEvent.type === "utterance_interrupted" ||
            nextEvent.type === "user_input");

        return (
          <EventWrapper key={index} bgColor="bg-white">
            <EventComponent
              event={event}
              interruptedMessage={
                isNextEventInterrupted
                  ? (nextEvent as UtteranceInterruptedEvent).details
                      .interrupted_at
                  : ""
              }
            />
          </EventWrapper>
        );
      })}
    </>
  );
}
